import React, { useCallback, useEffect, useState } from "react";
import {
  fullwidthTohalfWidth,
  normalizesDashedNumber,
} from "../../../shared/lib/numberUtils";
import {
  GroupedPrefectures,
  SharedArchitecturalSoftwares,
} from "../../../shared/lib/types";
import { Options } from "../../shared/lib/types";
import useFlash from "../../shared/lib/useFlash";
import { Step1FormData } from "../owned_business_entities/lib/useStep1Form";
import { Step2FormData } from "../owned_business_entities/lib/useStep2Form";
import BusinessEntityShowContent from "../shared/components/BusinessEntityShowContent";
import BusinessEntityShowPreviewLayout from "../shared/components/BusinessEntityShowPreviewLayout";

const BusinessEntityPreviewsShow = ({
  prefectures,
  industries,
  businessFields,
  groupedPrefectures,
  buildingTypes,
  structureTypes,
  architecturalSoftwares,
}: {
  prefectures: Options;
  industries: Options;
  businessFields: Options;
  groupedPrefectures: GroupedPrefectures;
  buildingTypes: Options;
  structureTypes: Options;
  architecturalSoftwares: SharedArchitecturalSoftwares;
}) => {
  const [previewData, setPreviewData] = useState<{
    businessEntity: Step1FormData & Step2FormData;
    logoImage: string;
    designedArchitecturalWorksImages: { url: string; signed_id: string }[][];
  }>();

  const showFlash = useFlash();
  const showError = useCallback(
    () =>
      showFlash({
        error: "プレビューが表示できません。タブを閉じてください。",
      }),
    [showFlash],
  );

  useEffect(() => {
    const businessEntityPreviewParams = localStorage.getItem(
      "businessEntityPreviewParams",
    );
    if (!businessEntityPreviewParams) {
      showError();
      return;
    }
    const parsedParams = JSON.parse(businessEntityPreviewParams) as {
      businessEntity: Step1FormData & Step2FormData;
      logoImage: string;
      designedArchitecturalWorksImages: { url: string; signed_id: string }[][];
      createdAt: number;
    };
    if (new Date().getTime() - parsedParams.createdAt > 5 * 60 * 1000) {
      showError();
      return;
    }

    setPreviewData(parsedParams);
  }, [showError]);

  if (previewData == null) return;

  const { businessEntity, logoImage, designedArchitecturalWorksImages } =
    previewData;

  const client_info = businessEntity.client_info_attributes
    ? {
        ...businessEntity.client_info_attributes,
        desired_industries:
          businessEntity.client_info_attributes.desired_industry_ids.map((id) =>
            optionObject({ id, options: industries }),
          ),
        desired_business_fields:
          businessEntity.client_info_attributes.desired_business_field_ids.map(
            (id) => optionObject({ id, options: businessFields }),
          ),
      }
    : null;

  const contractor_info = businessEntity.contractor_info_attributes
    ? {
        ...businessEntity.contractor_info_attributes,
        desired_industries:
          businessEntity.contractor_info_attributes.desired_industry_ids.map(
            (id) => optionObject({ id, options: industries }),
          ),
        supported_business_fields:
          businessEntity.contractor_info_attributes.supported_business_field_ids.map(
            (id) => optionObject({ id, options: businessFields }),
          ),
        supported_building_types:
          businessEntity.contractor_info_attributes.contractor_info_supported_building_types_attributes.map(
            (building_type) => ({
              ...optionObject({
                id: building_type.building_type_id,
                options: buildingTypes,
              }),
              structure_types: building_type.structure_type_ids.map((id) =>
                optionObject({ id, options: structureTypes }),
              ),
            }),
          ),
        supported_prefectures: groupedPrefectures
          .map((regionGroup) => {
            const regionPrefectures = regionGroup.prefectures.filter(
              (prefecture) =>
                businessEntity.contractor_info_attributes?.supported_prefecture_ids?.includes(
                  prefecture.id.toString(),
                ),
            );

            return regionPrefectures.length > 0
              ? {
                  region: regionGroup.region,
                  prefectures: regionPrefectures,
                }
              : null;
          })
          .filter((v) => v !== null),
        architectural_design_works:
          businessEntity.contractor_info_attributes.architectural_design_works_attributes
            .map((work, originalIndex) => ({ work, originalIndex }))
            .filter(({ work }) => work.published)
            .map(({ work, originalIndex }) => ({
              ...work,
              prefecture: optionObject({
                id: work.prefecture_id,
                options: prefectures,
              }),
              building_type: optionObject({
                id: work.building_type_id,
                options: buildingTypes,
              }),
              structure_types: work.structure_type_ids.map((id) =>
                optionObject({ id, options: structureTypes }),
              ),
              above_ground_floors: work.above_ground_floors
                ? parseInt(fullwidthTohalfWidth(work.above_ground_floors))
                : null,
              underground_floors: work.underground_floors
                ? parseInt(fullwidthTohalfWidth(work.underground_floors))
                : null,
              total_floor_area: work.total_floor_area
                ? parseInt(fullwidthTohalfWidth(work.total_floor_area))
                : null,
              completion_year: work.completion_year
                ? parseInt(fullwidthTohalfWidth(work.completion_year))
                : null,
              images: designedArchitecturalWorksImages[originalIndex]?.map(
                (image) => {
                  if (image instanceof File) {
                    return { url: URL.createObjectURL(image), signed_id: "" };
                  } else {
                    return image;
                  }
                },
              ),
            })),
      }
    : null;

  return (
    <BusinessEntityShowPreviewLayout>
      <BusinessEntityShowContent
        businessEntity={{
          ...businessEntity,
          code: "",
          is_own: false,
          logo_image_url: logoImage,
          number_of_employees:
            parseInt(
              fullwidthTohalfWidth(businessEntity.number_of_employees),
            ) || 0,
          used_architectural_software_categories: architecturalSoftwares
            .map(({ category, items }) => {
              const categorySoftwares = items.filter((item) =>
                businessEntity.used_architectural_software_ids.includes(
                  item.id.toString(),
                ),
              );
              return categorySoftwares.length > 0
                ? {
                    category,
                    softwares: categorySoftwares.map((software) => ({
                      id: software.id,
                      name: software.name,
                    })),
                  }
                : null;
            })
            .filter((v) => v !== null),
          capital_ten_thousand_yen:
            parseInt(
              fullwidthTohalfWidth(businessEntity.capital_ten_thousand_yen),
            ) || 0,
          postal_code: normalizesDashedNumber(businessEntity.postal_code),
          address2: normalizesDashedNumber(businessEntity.address2),
          prefecture: optionObject({
            id: businessEntity.prefecture_id,
            options: prefectures,
          }),
          industry: optionObject({
            id: businessEntity.industry_id,
            options: industries,
          }),
          establishment_year_month: `${businessEntity.establishment_year ?? ""}/${businessEntity.establishment_month ?? ""}`,
          client_info,
          contractor_info,
        }}
        preview
      />
    </BusinessEntityShowPreviewLayout>
  );
};

const optionObject = ({
  id,
  options,
}: {
  id: string | undefined;
  options: Options;
}) => {
  return {
    id: parseInt(id ?? "0"),
    name:
      options.find((option) => option.id === parseInt(id ?? "0"))?.name ?? "",
  };
};

export default BusinessEntityPreviewsShow;
